import { useCallback } from "react";

const useArrowKeysFieldSelection = ({ inputRefs, gridDimensions }) => {
  const getFieldKey = (rowIndex, colIndex) => `${rowIndex}-${colIndex}`;
  const getCursorPosition = (element) => element.selectionStart;
  const isCursorAtStart = useCallback((element) => getCursorPosition(element) === 0, []);
  const isCursorAtEnd = useCallback(
    (element) => getCursorPosition(element) === element.value.length,
    []
  );
  const focusField = useCallback((rowIndex, colIndex) => {
    const key = getFieldKey(rowIndex, colIndex);
    const inputElement = inputRefs.current[key];
    if (inputElement) {
      inputElement.focus();
      // Optional: select all text when navigating with arrow keys
      // inputElement.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getNextFieldPosition = useCallback(
    (currentRow, currentCol, direction) => {
      const { rows, cols } = gridDimensions;
      let newRow = currentRow;
      let newCol = currentCol;

      switch (direction) {
        case "up":
          newRow = Math.max(0, currentRow - 1);
          break;
        case "down":
          newRow = Math.min(rows - 1, currentRow + 1);
          break;
        case "left":
          newCol = Math.max(0, currentCol - 1);
          break;
        case "right":
          newCol = Math.min(cols - 1, currentCol + 1);
          break;
        case "next":
          if (currentCol < cols - 1) {
            newCol = currentCol + 1;
          } else if (currentRow < rows - 1) {
            newRow = currentRow + 1;
            newCol = 0;
          }
          break;
        case "previous":
          if (currentCol > 0) {
            newCol = currentCol - 1;
          } else if (currentRow > 0) {
            newRow = currentRow - 1;
            newCol = cols - 1;
          }
          break;
        default:
          break;
      }

      return { row: newRow, col: newCol };
    },
    [gridDimensions]
  );
  const handleKeyDown = useCallback(
    (event, currentRow, currentCol) => {
      const { key, shiftKey } = event;
      const inputElement = event.target;
      const { rows, cols } = gridDimensions;

      let direction = null;
      let shouldPreventDefault = false;

      switch (key) {
        case "ArrowUp":
          if (currentRow > 0) {
            direction = "up";
            shouldPreventDefault = true;
          }
          break;

        case "ArrowDown":
          if (currentRow < rows - 1) {
            direction = "down";
            shouldPreventDefault = true;
          }
          break;

        case "ArrowLeft":
          if (isCursorAtStart(inputElement) && currentCol > 0) {
            direction = "left";
            shouldPreventDefault = true;
          }
          break;

        case "ArrowRight":
          if (isCursorAtEnd(inputElement) && currentCol < cols - 1) {
            direction = "right";
            shouldPreventDefault = true;
          }
          break;

        case "Tab":
          // Handle Tab navigation (forward/backward)
          direction = shiftKey ? "previous" : "next";
          shouldPreventDefault = true;
          break;

        case "Enter":
          // Move to next row, same column (or next field if at end)
          if (currentRow < rows - 1) {
            direction = "down";
          } else {
            direction = "next";
          }
          shouldPreventDefault = true;
          break;

        case "Escape":
          // Optional: blur the current field
          inputElement.blur();
          shouldPreventDefault = true;
          break;

        case "a":
          event.stopPropagation();
          break;

        default:
          break;
      }

      if (direction && shouldPreventDefault) {
        event.preventDefault();
        const { row: newRow, col: newCol } = getNextFieldPosition(
          currentRow,
          currentCol,
          direction
        );

        if (newRow !== currentRow || newCol !== currentCol) {
          setTimeout(() => {
            focusField(newRow, newCol);
          }, 0);
        }
      }
    },
    [gridDimensions, isCursorAtStart, isCursorAtEnd, getNextFieldPosition, focusField]
  );

  return { getFieldKey, handleKeyDown };
};

export default useArrowKeysFieldSelection;
